<template>
  <div>
    <vue-element-loading :active="isLoading" />
    <v-card>
      <v-card-title>
        <v-icon color="primary"> mdi-bus-clock </v-icon>
        <span class="pl-2">{{ $t('pages.student_reports') }}</span>
        <v-spacer></v-spacer>
      </v-card-title>
      <v-data-table
        :headers="headers"
        :items="reports"
        :options.sync="options"
        :server-items-length="totalReports"
        class="elevation-1"
      >
        <template v-slot:top>
          <div class="mx-2 row">
            <div class="col-xs-12 col-sm-6 col-md-2">
              <v-autocomplete
                v-model="bus_id"
                :items="buses"
                item-text="name"
                item-value="id"
                clearable
                dense
                outlined
                :label="$t('forms.buses')"
              ></v-autocomplete>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-2">
              <v-autocomplete
                v-model="driver_id"
                :items="drivers"
                item-text="name"
                item-value="id"
                clearable
                dense
                outlined
                :label="$t('forms.driver')"
              ></v-autocomplete>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-2">
              <v-autocomplete
                v-model="student_id"
                :items="students"
                item-text="name"
                item-value="id"
                clearable
                dense
                outlined
                :label="$t('forms.students')"
              ></v-autocomplete>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-2">
              <v-menu
                v-model="started_at_menu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="started_at"
                    :label="$t('forms.started_at')"
                    append-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                    clearable
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="started_at"
                  @input="started_at_menu = false"
                ></v-date-picker>
              </v-menu>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-2">
              <v-menu
                v-model="finished_at_menu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="finished_at"
                    :label="$t('forms.finished_at')"
                    append-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                    clearable
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="finished_at"
                  @input="finished_at_menu = false"
                ></v-date-picker>
              </v-menu>
            </div>
            <div class="col-xs-12 col-sm-6 col-md-2">
              <v-menu
                v-model="rejected_at_menu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="rejected_at"
                    :label="$t('forms.rejected_at')"
                    append-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                    clearable
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="rejected_at"
                  @input="rejected_at_menu = false"
                ></v-date-picker>
              </v-menu>
            </div>
          </div>
        </template>

        <template v-slot:item.driver_name="{ item }">
          <v-avatar rounded size="38">
            <v-img :src="getAvatarUrl(item.driver_avatar)"></v-img>
          </v-avatar>
          {{ item.driver_name }}
        </template>

        <template v-slot:item.student_name="{ item }">
          <v-avatar rounded size="38">
            <v-img :src="getAvatarUrl(item.student_avatar)"></v-img>
          </v-avatar>
          {{ item.student_name }}
        </template>

        <template v-slot:item.started_at="{ item }">
          <small v-if="item.started_at">
            {{ item.started_at | moment('DD/MM/YYYY') }}
          </small>
          -
          <small class="text-muted" v-if="item.started_at">
            {{ item.started_at | moment('HH:mm') }}
          </small>
        </template>

        <template v-slot:item.finished_at="{ item }">
          <small v-if="item.finished_at">
            {{ item.finished_at | moment('DD/MM/YYYY') }}
          </small>
          -
          <small class="text-muted" v-if="item.finished_at">
            {{ item.finished_at | moment('HH:mm') }}
          </small>
        </template>

        <template v-slot:item.rejected_at="{ item }">
          <small v-if="item.rejected_at">
            {{ item.rejected_at | moment('DD/MM/YYYY') }}
          </small>
          -
          <small class="text-muted" v-if="item.rejected_at">
            {{ item.rejected_at | moment('HH:mm') }}
          </small>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import VueElementLoading from 'vue-element-loading';
import { Keys } from '@/config';

export default {
  components: { VueElementLoading },
  data() {
    return {
      isLoading: false,
      reports: [],
      headers: [
        { text: this.$t('table.bus'), value: 'bus_name' },
        { text: this.$t('table.driver'), value: 'driver_name' },
        { text: this.$t('table.student'), value: 'student_name' },
        { text: this.$t('table.started_at'), value: 'started_at' },
        { text: this.$t('table.finished_at'), value: 'finished_at' },
        { text: this.$t('table.rejected_at'), value: 'rejected_at' },
      ],
      options: {},
      totalReports: 0,
      buses: [],
      bus_id: null,
      drivers: [],
      driver_id: null,
      students: [],
      student_id: null,
      started_at: null,
      started_at_menu: null,
      finished_at: null,
      finished_at_menu: null,
      rejected_at: null,
      rejected_at_menu: null,
    };
  },
  mounted() {
    // this.loadReports();
    this.getAllBuses();
    this.getAllDrivers();
    this.getAllStudents();
  },
  methods: {
    loadReports() {
      this.isLoading = true;
      const params = {
        page: this.options?.page,
        per_page: this.options?.itemsPerPage,
        order_by: this.options?.sortBy?.[0],
        order_direction:
          this.options?.sortDesc?.[0] === null ||
          this.options?.sortDesc?.[0] === undefined
            ? null
            : this.options?.sortDesc?.[0] === true
            ? 'DESC'
            : 'ASC',
        bus_id: this.bus_id,
        driver_id: this.driver_id,
        student_id: this.student_id,
        started_at: this.started_at,
        finished_at: this.finished_at,
        rejected_at: this.rejected_at,
      };
      axios
        .get(`/rides/student-reports`, { params })
        .then((response) => {
          this.reports = response?.data?.data;
          this.totalReports = response?.data?.total;
        })
        .catch(() => {
          this.$notify({
            title: 'Error',
            text: 'Error while retrieving reports',
            type: 'error',
          });
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    getAvatarUrl(avatar) {
      if (avatar) {
        return (this.avatarImageUrl = Keys.VUE_APP_API_URL + avatar);
      } else {
        return (this.avatarImageUrl =
          Keys.VUE_APP_API_URL + '/storage/avatars/1/avatar.png');
      }
    },
    getAllBuses() {
      axios
        .get('/buses/list')
        .then((response) => {
          this.buses = response?.data;
        })
        .catch(() => {
          this.$notify({
            title: 'Error',
            text: 'Error fetching buses',
            type: 'error',
          });
        });
    },
    getAllDrivers() {
      axios
        .get('/buses/available-drivers')
        .then((response) => {
          this.drivers = response.data;
        })
        .catch(() => {
          this.$notify({
            title: 'Error',
            text: 'Error while retrieving drivers',
            type: 'error',
          });
        });
    },
    getAllStudents() {
      axios
        .get('/users/all-students-list')
        .then((response) => {
          this.students = response.data;
        })
        .catch(() => {
          this.$notify({
            title: 'Error',
            text: 'Error while retrieving drivers',
            type: 'error',
          });
        });
    },
  },
  watch: {
    options: {
      handler() {
        this.loadReports();
      },
      deep: true,
    },
    bus_id: function () {
      this.loadReports();
    },
    driver_id: function () {
      this.loadReports();
    },
    student_id: function () {
      this.loadReports();
    },
    started_at: function () {
      this.loadReports();
    },
    finished_at: function () {
      this.loadReports();
    },
    rejected_at: function () {
      this.loadReports();
    },
  },
};
</script>

<style lang="scss" scoped></style>
